import { type NotablePoint, notablePointsOf } from './notable';
import { parseExpression } from './parse';
import { sampleFunction } from './sample';

/**
 * Choosing the frame a graph is drawn in.
 *
 * ## The defect this replaces
 *
 * The frame used to be `max(|from|, |to|)` — the **x** range, and nothing else — applied to both
 * axes. So `graph "x^3-3*x+1" -2.5 2.5` was given a window reaching 2.5 in every direction while
 * the curve reaches ±9, and what came out was a cubic chopped into three apparent fragments. It
 * is not a drawing of the function; a class shown it cannot tell what function it is.
 *
 * Writing a bigger `axes` by hand does not fix it either, because one number governed both
 * directions: `axes 10` frames the height and leaves the curve a thin squiggle down the middle of
 * an empty page.
 *
 * ## What replaces it
 *
 * Two extents, chosen from the *sampled values*. The height comes from the curve, the width from
 * the interval it was asked for. A cubic then gets the tall narrow frame a textbook draws it in.
 *
 * ## Telling a tail from an asymptote
 *
 * `1/x` and `tan x` have no maximum worth framing: near a vertical asymptote a few samples reach
 * enormous values while the rest of the curve sits in a modest band, and framing the spike
 * flattens everything else onto the axis. A polynomial is the opposite — its largest values are
 * at the ends of the interval and they are the whole shape.
 *
 * The first version of this told them apart by comparing the maximum against a percentile, and it
 * was wrong on the commonest function in the syllabus: a cubic's tail is *also* far above the
 * bulk, so `y = x³ − 3x + 1` was judged to have an asymptote and came out clipped — the very
 * defect this file exists to remove.
 *
 * The sampler already knows the answer and knows it exactly. It **breaks the curve into separate
 * runs** at a discontinuity, precisely so a graph is not drawn with a vertical line through its
 * asymptote. One run means the function is continuous across the interval and its maximum is a
 * feature; more than one means something is escaping, and the bulk is what deserves the page.
 *
 * A textbook makes the same call by hand: it draws `y = (2x+1)/(x−1)` with the branches leaving
 * the top and bottom of the frame, not with a frame tall enough to contain them.
 */

/**
 * How much of the curve the frame is required to hold.
 *
 * Not all of it, and that is the whole idea. Framing the largest value means framing a *tail*,
 * and a tail is the least informative part of a graph — it carries one bit, which way it goes,
 * and the reader gets that from the direction at the edge. Insisting on containing it costs the
 * part that matters: `y = x³ − 3x + 1` reaches ±9 at the ends while its two turning points, the
 * entire subject of the exercise, sit at 3 and −1. A frame built for the ±9 squeezes them into a
 * centimetre.
 *
 * Eighty-five per cent lands, on the functions in the syllabus, almost exactly where a textbook
 * puts the frame by hand.
 */
const BULK_PERCENTILE = 0.85;

/**
 * A frame taller than this many times its width is a column, not a graph.
 *
 * Set from what a textbook does rather than from what fits: past it the curve becomes a thin
 * vertical stroke and whatever the exercise is actually about is squeezed into the middle
 * centimetre.
 *
 * What it *does* changed. It used to cap the height, which meant cutting the curve — and it did
 * not even manage that, because the cap was applied before {@link windowAround} pulls the origin
 * into the frame, and pulling the origin in is what makes a frame tall. `y = −x²` over `[−10; 10]`
 * came out at 3.3 against this limit of 2.5: the trimmed band `[−71; −15]` was stretched to
 * `[−74.5; 0]`, which doubled the height and left the x axis lying exactly on the top edge.
 *
 * Now it is the point at which the **y axis takes a unit of its own** — see `yScale` — so the
 * whole curve is kept and the page is what gives. Cutting is reserved for a curve that genuinely
 * escapes, which is a different thing and has its own test in {@link OUTLIER_RATIO}.
 */
const MAX_ASPECT = 2.5;

/**
 * How tall a frame is allowed to end up once the y axis has been given its own unit.
 *
 * Slightly taller than wide, which is where a textbook puts a graph that has to be tall. Coming
 * to rest exactly on `MAX_ASPECT` would mean every rescaled figure sat at the limit of what is
 * readable rather than comfortably inside it.
 */
const COMFORTABLE_ASPECT = 1.5;

/** Ticks are unreadable past this many each way, whatever the extent. */
const MAX_TICKS = 12;

export interface GraphWindow {
  /** Half-width, in mathematical units. */
  readonly extentX: number;
  /** Half-height. Equal to `extentX` only by coincidence. */
  readonly extentY: number;
  /**
   * The middle of the frame. The origin only when the problem happens to be centred there.
   *
   * ## Why the frame moved off the origin
   *
   * It never was the right place for it. `y = x² − 4x + 3` over `x ∈ [−1; 5]` needs 5 to the
   * right and 1 to the left; a frame symmetric about the origin has to reach 5 both ways, so
   * **half the picture is blank paper** and the curve is drawn at half the size the page allows.
   * Measured on that exact figure: 6 units of frame carrying a curve 6 wide, in a picture 12
   * wide. A textbook draws the part of the plane the problem is about.
   *
   * The origin is always kept inside — see {@link windowAround}. It is not a decoration: the
   * axes are drawn along `x = 0` and `y = 0`, and a frame that excluded the origin would be a
   * pair of axes nobody can see.
   */
  readonly center: { readonly x: number; readonly y: number };
  /** Units between ticks on the x axis. */
  readonly step: number;
  /**
   * Units between ticks on the y axis, chosen from its own extent.
   *
   * One step used to serve both, taken from the longer axis on the grounds that it is the one
   * whose labels crowd. That is true and it is the wrong conclusion: a step that keeps a long
   * axis readable is far too coarse for a short one, and far too fine the other way round. The
   * figure that made it plain had 37 numbers stacked down its y axis and five along its x.
   */
  readonly stepY: number;
  /**
   * The y axis's unit, as a fraction of the x axis's. `1` means squares stay square.
   *
   * ## Why a graph may have unequal units and a geometry figure may not
   *
   * A circle drawn on unequal axes is an ellipse, so for a figure about *shapes* this must stay
   * 1 — and it does, because nothing sets it but the two paths that fit axes to a **function**.
   *
   * For a function it is the difference between a figure and a column. `y = −x²` over `[−10; 10]`
   * is a hundred units tall and twenty wide; with one unit for both axes there is no framing of
   * it that is not a tower, and the only lever left is to cut the curve. A textbook does not cut
   * the curve — it writes a different number on the y axis. This is that number.
   *
   * Never above 1: it may compress a tall figure but never stretch a flat one. `y = sin x` over
   * `[−6.5; 6.5]` belongs in a wide short frame, and stretching it to fill a square would be
   * inventing a shape the function does not have.
   */
  readonly yScale: number;
}

/** The frame's edges, which is what most callers actually want to ask about. */
export function windowBox(window: GraphWindow): {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
} {
  return {
    minX: window.center.x - window.extentX,
    maxX: window.center.x + window.extentX,
    minY: window.center.y - window.extentY,
    maxY: window.center.y + window.extentY,
  };
}

/**
 * 1, 2, 5, 10, 20, 50… — the steps a person reads without doing arithmetic.
 *
 * Aimed at about five ticks each way across the frame. Eight was the first guess and it produced
 * `−2.5 −2 −1.5 −1 −0.5` crammed along a short axis with the numbers touching — legible on
 * screen at full zoom and a grey smear on a printed worksheet, which is where these figures
 * actually end up.
 */
function niceStep(extent: number): number {
  const rough = extent / 5;
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(rough, 1e-6)));
  for (const multiple of [1, 2, 5]) {
    if (rough <= multiple * magnitude) return multiple * magnitude;
  }
  return 10 * magnitude;
}

function percentile(sorted: readonly number[], fraction: number): number {
  if (sorted.length === 0) return 0;
  const at = Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * fraction)));
  return sorted[at] ?? 0;
}

/** Air round the content, so nothing is drawn touching the edge of its own frame. */
const MARGIN = 0.06;

/**
 * A frame holding `[low, high]` with a little air, and the origin.
 *
 * ## The origin is not optional
 *
 * `x ∈ [3; 7]` framed on its own would draw a y axis nobody can see and number an x axis with no
 * zero on it — not a picture a student can read a coordinate off. Textbooks that genuinely need a
 * window away from the origin draw a *broken* axis, which is a different figure from this one.
 *
 * ## And it is no longer snapped out to whole steps
 *
 * It was, so that the frame would end on a tick. That cost more than it bought: `sin x` over
 * `[−6.5; 6.5]` with a step of 5 rounded out to `[−10; 10]`, and the curve — which never leaves
 * ±1 — became a small wiggle in a box twenty units wide and ten tall. `buildAxes` now finds its
 * ticks at multiples of the step *inside* whatever frame it is given, so the frame is free to end
 * wherever the content does.
 *
 * ## The origin gets air too
 *
 * It used to be pulled in flush. On a curve that stays one side of the axis — `y = −x²`, and half
 * the exercises in a chapter on parabolas — that put the frame's edge exactly on `y = 0`, so the
 * x axis was drawn along the top border of the picture with its numbers hard against the edge and
 * nothing above it. What looked from outside like "the axes are only half drawn" was this: the
 * axis was there, at the boundary, with no page on the other side of it.
 *
 * So the origin is brought in with the same margin everything else gets.
 *
 * Returned as a centre and a half-width because that is what `AxesSource` holds: one field for
 * where, one for how far, instead of two edges to keep consistent with each other.
 */
function windowAround(
  low: number,
  high: number
): { readonly center: number; readonly extent: number } {
  const margin = Math.max((high - low) * MARGIN, 1e-6);
  const min = Math.min(-margin, low - margin);
  const max = Math.max(margin, high + margin);

  if (max - min < 1e-6) return { center: 0, extent: 1e-6 };
  return { center: (min + max) / 2, extent: (max - min) / 2 };
}

/**
 * The stretch of `x` a curve is worth drawing over, inside the range it is allowed.
 *
 * ## Why the x range needs fitting too, and why only sometimes
 *
 * Everything above frames the *height*. The width was taken as given, and for a curve somebody
 * has actually specified that is exactly right — the codebase is emphatic about it, and so is a
 * teacher: a problem that says `[−1; 5]` means all of it. Nothing here touches a stated range.
 *
 * A **default** range is a different thing. Nobody chose `[−10; 10]`; it is what the editor uses
 * when asked to draw a formula and nothing else. On a steep curve it is far too wide, and the
 * result is the failure this file already names elsewhere — a figure quietly answering about a
 * different domain. Measured on `y = 2x³ + 5x² + 5x + 2`: the curve is inside its own frame for
 * `x ∈ [−2; 5]` while the axis is numbered to ±10, so two thirds of the width is blank paper and
 * the curve simply stops partway along an axis that says it continues.
 *
 * ## How wide
 *
 * The way a teacher picks it: **the features, plus half again either side.** The features are
 * where the exercise is — the roots, the turning points, the value at `x = 0` — and a figure that
 * showed only them would be a fragment, so the window is opened out around them until the reader
 * can see which way the curve leaves.
 *
 * The floor matters as much as the ratio. A curve whose notable points sit almost on top of each
 * other — a monotonic cubic has one root and one intercept and nothing else — would otherwise be
 * drawn through a slit. `y = 2x³ + 5x² + 5x + 2` has its two at `x = −1` and `x = 0`; half again
 * either side of that is a window a centimetre wide, and what it needs is a couple of units.
 *
 * It can only ever **narrow**. The range it is given is the outer bound, so a curve is never
 * drawn outside what was asked for, and a function with no notable points at all — `1/x` has
 * none — keeps exactly the range it had.
 */
export function fittedDomain(
  expression: string,
  from: number,
  to: number
): { readonly from: number; readonly to: number } {
  if (!(to > from)) return { from, to };

  let notable: readonly NotablePoint[];
  try {
    notable = notablePointsOf(parseExpression(expression), from, to);
  } catch {
    return { from, to };
  }

  const xs = notable.map((point) => point.x).filter((x) => Number.isFinite(x));
  if (xs.length === 0) return { from, to };

  const low = Math.min(...xs);
  const high = Math.max(...xs);
  const margin = Math.max(MIN_DOMAIN_MARGIN, (high - low) * DOMAIN_MARGIN);

  return { from: Math.max(from, low - margin), to: Math.min(to, high + margin) };
}

/** How far past the outermost feature the window reaches, as a fraction of their spread. */
const DOMAIN_MARGIN = 0.5;

/**
 * The narrowest that margin may be, in mathematical units.
 *
 * For a curve whose features are almost coincident the fraction gives nothing, and a couple of
 * units either side is what turns a slit into a figure.
 */
const MIN_DOMAIN_MARGIN = 1.5;

/**
 * The frame for `y = f(x)` over `[from, to]`.
 *
 * Falls back to a square frame when the expression cannot be parsed or produces nothing finite —
 * a graph that draws nothing should still leave axes a reader can see, rather than no figure at
 * all.
 */
export function graphWindow(expression: string, from: number, to: number): GraphWindow {
  let samples: readonly (readonly { readonly y: number }[])[] = [];
  try {
    samples = sampleFunction(parseExpression(expression), { from, to });
  } catch {
    // An unparseable formula draws nothing, and the fall-through below still gives it axes.
  }

  return graphWindowOf(samples, from, to);
}

/**
 * The same frame, for a caller that has already sampled the curve.
 *
 * The evaluator has. It plots the function and then has to decide how much of the result to
 * keep, and the answer must be *this* frame and not a second opinion — a curve clipped to one
 * height inside axes drawn to another is a figure that shows the reader two thirds of the
 * interval it claims to show. Handing the samples back rather than re-deriving them is what
 * makes the two agree by construction instead of by coincidence, and it costs one traversal
 * rather than a second six-hundred-point evaluation on every drag.
 */
export function graphWindowOf(
  samples: readonly (readonly { readonly y: number }[])[],
  from: number,
  to: number
): GraphWindow {
  const across = windowAround(Math.min(from, to), Math.max(from, to));

  const values = samples
    .flat()
    .map((point) => point.y)
    .filter((height) => Number.isFinite(height))
    .sort((a, b) => a - b);

  /*
   * The whole curve when it fits, and the bulk of it when it does not.
   *
   * These are two different situations and one rule was wrong for both. A problem that states an
   * interval — `y = x²−4x+3` on `[−1; 5]` — means the reader to see all of it, and trimming the
   * top 7% cut the last sliver off both ends: the plotted range came back as [−0.86; 4.86] for an
   * interval stated as [−1; 5], which is a figure quietly answering about a different domain.
   *
   * A curve that escapes is the other case. `(2x+1)/(x−1)` runs to infinity beside its pole and
   * `x³−3x` reaches ±9 at the ends of a 5-wide interval; framing either in full makes a column
   * with the interesting part squeezed into the middle centimetre. There the bulk is what
   * deserves the page and the tails are let out of the frame, which is what a textbook does.
   *
   * The test between them is simply **whether it fits** — `MAX_ASPECT`. An earlier attempt asked
   * the sampler how many runs it had produced, on the theory that a broken curve is an escaping
   * one; that is true and insufficient, because a cubic's tail escapes the page without breaking
   * the curve.
   *
   * Trimming is still the right answer here even though the y axis can now take a unit of its
   * own. Compressing instead would keep the tails and pay for them out of the middle: on
   * `x³−3x+1` the two turning points, which are the whole exercise, would be left holding a fifth
   * of the height. A tail carries one bit and the middle carries the lesson.
   */
  const trim = (1 - BULK_PERCENTILE) / 2;
  const room = across.extent * 2 * MAX_ASPECT;

  const whole =
    values.length === 0
      ? { low: 0, high: 0 }
      : { low: values[0] ?? 0, high: values[values.length - 1] ?? 0 };

  const { low, high } =
    values.length === 0 || whole.high - whole.low <= room
      ? whole
      : { low: percentile(values, trim), high: percentile(values, 1 - trim) };

  /*
   * Capped, but not floored.
   *
   * The first version also refused to be shorter than it was wide, meaning to stop a gentle
   * function being drawn in a letterbox. It drew `y = sin x` over `[−6.5; 6.5]` in a frame seven
   * units tall for a curve that never leaves ±1 — a flat squiggle across an empty page. A wide
   * short frame is exactly how a textbook draws a sine, so the floor was the mistake, not the
   * letterbox.
   *
   * The ceiling stays, and it is worth saying what it does **not** do: it does not settle the
   * shape of the finished figure. `windowAround` runs after it and pulls the origin in, which on
   * a curve that stays one side of the axis roughly doubles the height again — `y = −x²` came out
   * at 3.3 against a cap of 2.5. Bringing the drawn box back under the limit is `yScale`'s job,
   * below, and it does it without costing any more of the curve.
   */
  const middle = (low + high) / 2;
  const half = Math.min((high - low) / 2, room / 2);
  const upright = windowAround(middle - half, middle + half);

  const stepX = ticksFor(across.extent);
  const stepY = ticksFor(upright.extent);

  /*
   * Far enough the other way to carry a number.
   *
   * The two directions are deliberately **not** equal — a frame symmetric about the origin
   * spends half the page on empty plane, which is the argument `center` makes at length. But
   * there is a difference between a short negative reach and a vestigial one, and `windowAround`
   * was leaving the second: six per cent of the height, which on a curve that stays above the
   * axis is less than one step, so the negative direction carried no number at all.
   *
   * An axis with numbers on one side of the origin and nothing on the other does not read as a
   * deliberate frame; it reads as an axis that has been cut off. One step is the smallest reach
   * that answers it, because one step is exactly what it takes to put a number there.
   */
  const spanX = reachingBothWays(across.center, across.extent, stepX);
  const spanY = reachingBothWays(upright.center, upright.extent, stepY);

  /*
   * Where the y axis stops sharing the x axis's unit.
   *
   * Only when the figure would otherwise be a column, and only downwards — see `yScale`. The
   * arithmetic is the whole of it: the drawn box is `extentX·scale` wide and `extentY·scale·yScale`
   * tall, so asking for a given height-to-width is asking for `yScale = wanted / aspect`.
   */
  const aspect = spanY.extent / spanX.extent;
  const yScale = aspect > MAX_ASPECT ? COMFORTABLE_ASPECT / aspect : 1;

  return {
    extentX: spanX.extent,
    extentY: spanY.extent,
    center: { x: spanX.center, y: spanY.center },
    step: stepX,
    stepY,
    yScale,
  };
}

/**
 * A frame opened out until it reaches at least one step either side of the origin.
 *
 * Only ever widens. The frame it is given already contains the origin — `windowAround` sees to
 * that — so this is about how far *past* it the axis goes, and the answer has to be far enough
 * for a number. It also subsumes the older rule that a frame must be at least one step tall,
 * which was the same requirement stated for only one of the two directions.
 */
function reachingBothWays(
  center: number,
  extent: number,
  step: number
): { readonly center: number; readonly extent: number } {
  const low = Math.min(center - extent, -step);
  const high = Math.max(center + extent, step);
  return { center: (low + high) / 2, extent: (high - low) / 2 };
}

/** A step that is both a round number and coarse enough to keep the labels apart. */
function ticksFor(extent: number): number {
  return Math.max(niceStep(extent), extent / MAX_TICKS);
}
